import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import { Trash2, Plus, Image as ImageIcon, Video, Lock, Upload, Loader2, ArrowLeft, ChevronUp, ChevronDown, GripVertical, Edit2, Check, X, Monitor, Smartphone, Clock } from 'lucide-react';
import axios from 'axios';
import ConnectedDisplays from '../components/ConnectedDisplays';
import WeatherZipConfig from '../components/WeatherZipConfig';
import SlideTiming from '../components/SlideTiming';
import FullyKioskControl from '../components/FullyKioskControl';
import DefaultAdToggle from '../components/DefaultAdToggle';
import AlertConfig from '../components/AlertConfig';
import LivePreview from '../components/LivePreview';
import ZoneConfig from '../components/ZoneConfig';
import WhatsNew from '../components/WhatsNew';
import LiveStreamConfig from '../components/LiveStreamConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to convert YouTube URLs to embed format
const getYouTubeEmbedUrl = (url) => {
  if (!url) return url;
  if (url.includes('/embed/')) return url;
  
  let videoId = null;
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) videoId = watchMatch[1];
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) videoId = shortMatch[1];
  const vMatch = url.match(/youtube\.com\/v\/([^?&]+)/);
  if (vMatch) videoId = vMatch[1];
  
  if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  return url;
};

const DynamicAdmin = () => {
  const { pageId } = useParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [pageConfig, setPageConfig] = useState(null);
  const [content, setContent] = useState([]);
  const [newItemUrl, setNewItemUrl] = useState('');
  const [newItemType, setNewItemType] = useState('image');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [uploadMode, setUploadMode] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [correctPassword, setCorrectPassword] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  
  // Orientation state
  const [orientation, setOrientation] = useState('landscape');
  
  // Password management state
  const [pagePassword, setPagePassword] = useState('');
  const [showPasswordField, setShowPasswordField] = useState(false);
  
  // Playlist state
  const [usePlaylists, setUsePlaylists] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [playlistName, setPlaylistName] = useState('');
  const [playlistStartHour, setPlaylistStartHour] = useState(0);
  const [playlistEndHour, setPlaylistEndHour] = useState(24);

  // Load page config to get password
  useEffect(() => {
    const loadPageConfig = async () => {
      try {
        const response = await axios.get(`${API}/admin/pages/${pageId}`);
        setPageConfig(response.data);
        setCorrectPassword(response.data.password || '');
        setContent(response.data.items || []);
        setOrientation(response.data.orientation || 'landscape');
        setPagePassword(response.data.password || '');
        setUsePlaylists(response.data.use_playlists || false);
        setPlaylists(response.data.playlists || []);
        
        // Check if already authenticated
        const auth = sessionStorage.getItem(`${pageId}AdminAuth`);
        if (auth === 'true') {
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Failed to load page config:', error);
        toast.error('Page not found');
      } finally {
        setLoading(false);
      }
    };
    
    loadPageConfig();
  }, [pageId]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === correctPassword) {
      setIsAuthenticated(true);
      sessionStorage.setItem(`${pageId}AdminAuth`, 'true');
      toast.success('Access granted!');
    } else {
      toast.error('Incorrect password');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem(`${pageId}AdminAuth`);
    setPassword('');
    toast.success('Logged out');
  };

  const saveContent = async (newContent) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { items: newContent });
      setContent(newContent);
      toast.success('Content saved!');
    } catch (error) {
      console.error('Failed to save content:', error);
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 1024 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 1GB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        const newItem = {
          type: response.data.type,
          url: `${BACKEND_URL}${response.data.url}`,
        };
        const updated = [...content, newItem];
        saveContent(updated);
        toast.success(`${response.data.type} uploaded successfully!`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAddItem = () => {
    if (!newItemUrl.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    const processedUrl = newItemType === 'video' 
      ? getYouTubeEmbedUrl(newItemUrl.trim())
      : newItemUrl.trim();

    const newItem = { type: newItemType, url: processedUrl, description: newItemDescription.trim() || '' };
    const updated = [...content, newItem];
    saveContent(updated);
    setNewItemUrl('');
    setNewItemDescription('');
  };

  const handleRemoveItem = (index) => {
    const updated = content.filter((_, i) => i !== index);
    saveContent(updated);
  };
  
  const startEditDescription = (index) => {
    setEditingIndex(index);
    setEditDescription(content[index].description || '');
  };
  
  const saveDescriptionEdit = (index) => {
    const updated = [...content];
    updated[index] = { ...updated[index], description: editDescription };
    saveContent(updated);
    setEditingIndex(null);
    setEditDescription('');
  };
  
  const cancelEditDescription = () => {
    setEditingIndex(null);
    setEditDescription('');
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const updated = [...content];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    saveContent(updated);
  };

  const handleMoveDown = (index) => {
    if (index === content.length - 1) return;
    const updated = [...content];
    [updated[index + 1], updated[index]] = [updated[index], updated[index + 1]];
    saveContent(updated);
  };

  // Orientation
  const toggleOrientation = async (newOrientation) => {
    setOrientation(newOrientation);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { orientation: newOrientation });
      toast.success(`Orientation set to ${newOrientation}`);
    } catch (error) {
      toast.error('Failed to update orientation');
    }
  };

  // Password management
  const updatePassword = async () => {
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { password: pagePassword });
      setCorrectPassword(pagePassword);
      setShowPasswordField(false);
      toast.success('Password updated!');
    } catch (error) {
      toast.error('Failed to update password');
    }
  };

  // Playlist functions
  const toggleUsePlaylists = async (enabled) => {
    setUsePlaylists(enabled);
    try {
      await axios.put(`${API}/admin/pages/${pageId}/use-playlists?enabled=${enabled}`);
      toast.success(enabled ? 'Playlist mode enabled' : 'Playlist mode disabled');
    } catch (error) {
      setUsePlaylists(!enabled);
      toast.error('Failed to update playlist mode');
    }
  };

  const savePlaylist = async () => {
    if (!playlistName.trim()) return;
    const newPlaylist = {
      name: playlistName.trim(),
      start_hour: parseInt(playlistStartHour),
      end_hour: parseInt(playlistEndHour),
      items: editingPlaylist?.items || []
    };
    let updatedPlaylists;
    if (editingPlaylist && editingPlaylist.index !== undefined) {
      updatedPlaylists = [...playlists];
      updatedPlaylists[editingPlaylist.index] = newPlaylist;
    } else {
      updatedPlaylists = [...playlists, newPlaylist];
    }
    try {
      await axios.put(`${API}/admin/pages/${pageId}/playlists`, { playlists: updatedPlaylists });
      setPlaylists(updatedPlaylists);
      setEditingPlaylist(null);
      setPlaylistName('');
      setPlaylistStartHour(0);
      setPlaylistEndHour(24);
      toast.success('Playlist saved!');
    } catch (error) {
      toast.error('Failed to save playlist');
    }
  };

  const deletePlaylist = async (index) => {
    const updatedPlaylists = playlists.filter((_, i) => i !== index);
    try {
      await axios.put(`${API}/admin/pages/${pageId}/playlists`, { playlists: updatedPlaylists });
      setPlaylists(updatedPlaylists);
      toast.success('Playlist deleted');
    } catch (error) {
      toast.error('Failed to delete playlist');
    }
  };

  const startEditPlaylist = (playlist, index) => {
    setEditingPlaylist({ ...playlist, index });
    setPlaylistName(playlist.name);
    setPlaylistStartHour(playlist.start_hour);
    setPlaylistEndHour(playlist.end_hour);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (!pageConfig) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-900 border-slate-800">
          <CardContent className="p-8 text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Page Not Found</h1>
            <p className="text-slate-400 mb-6">The page "{pageId}" does not exist.</p>
            <Link to="/master-admin">
              <Button className="bg-cyan-500 hover:bg-cyan-600">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go to Master Admin
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-900 border-slate-800">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-cyan-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-cyan-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                {pageConfig.name} Admin <span className="text-cyan-400 text-sm font-normal">Snowman 1</span>
              </h1>
              <p className="text-slate-400">Enter password to continue</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500"
                autoFocus
              />
              <Button type="submit" className="w-full bg-cyan-500 hover:bg-cyan-600 text-white">
                Login
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-800 text-center">
              <a href="/" className="text-slate-400 hover:text-cyan-400 text-sm">
                Back to Home
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin panel
  return (
    <div className="min-h-screen bg-slate-950 py-12 px-4">
      <WhatsNew />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              {pageConfig.name} <span className="text-cyan-400">Admin</span>{' '}
              <span className="text-cyan-400 text-sm font-normal">Snowman 1</span>
            </h1>
            <p className="text-slate-400">Manage images and videos for /{pageId} page</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            Logout
          </Button>
        </div>

        {/* Add New Item */}
        <Card className="bg-slate-900 border-slate-800 mb-8">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4">Add New Content</h3>

            <div className="flex gap-2 mb-4">
              <Button
                onClick={() => setUploadMode('url')}
                variant={uploadMode === 'url' ? 'default' : 'outline'}
                className={uploadMode === 'url' ? 'bg-cyan-500 hover:bg-cyan-600' : 'border-slate-700 text-slate-300'}
                size="sm"
              >
                Enter URL
              </Button>
              <Button
                onClick={() => setUploadMode('file')}
                variant={uploadMode === 'file' ? 'default' : 'outline'}
                className={uploadMode === 'file' ? 'bg-cyan-500 hover:bg-cyan-600' : 'border-slate-700 text-slate-300'}
                size="sm"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload File
              </Button>
            </div>

            {uploadMode === 'url' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      type="text"
                      value={newItemUrl}
                      onChange={(e) => setNewItemUrl(e.target.value)}
                      placeholder="Enter image URL, video URL, or YouTube link"
                      className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500"
                    />
                    <p className="text-slate-500 text-xs mt-1">Supports: Direct image/video URLs and YouTube links (youtube.com/watch?v=... or youtu.be/...)</p>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={newItemType}
                      onChange={(e) => setNewItemType(e.target.value)}
                      className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 flex-1"
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Input
                    type="text"
                    value={newItemDescription}
                    onChange={(e) => setNewItemDescription(e.target.value)}
                    placeholder="Description (optional) - e.g., 'Holiday promo', 'Menu board'"
                    className="bg-slate-950 border-slate-700 text-white placeholder:text-slate-500 flex-1"
                  />
                  <Button onClick={handleAddItem} className="bg-cyan-500 hover:bg-cyan-600 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center hover:border-cyan-500 transition-colors">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                    <Upload className={`w-12 h-12 mb-4 ${uploading ? 'text-cyan-400 animate-pulse' : 'text-slate-400'}`} />
                    <p className="text-white font-medium mb-1">
                      {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-slate-500 text-sm">
                      Images: JPG, PNG, GIF, WEBP | Videos: MP4, WEBM, MOV (Max 1GB)
                    </p>
                  </label>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Content List */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-4">
              Current Content ({content.length} items)
            </h3>

            {content.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-400">No content added yet</p>
                <p className="text-slate-500 text-sm mt-2">Add images or videos above to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {content.map((item, index) => (
                  <div key={index} className="bg-slate-950 p-4 rounded-lg border border-slate-800 flex items-start gap-4">
                    <div className="flex-shrink-0 flex flex-col items-center gap-1 pt-2">
                      <span className="text-slate-500 text-xs font-medium">#{index + 1}</span>
                      <GripVertical className="w-4 h-4 text-slate-600" />
                    </div>
                    
                    <div className="flex-shrink-0 w-24 h-16 bg-slate-800 rounded overflow-hidden">
                      {item.type === 'image' ? (
                        <img src={item.url} alt={item.description || `Image ${index + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Video className="w-8 h-8 text-red-400" /></div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {item.type === 'image' ? <ImageIcon className="w-4 h-4 text-cyan-400" /> : <Video className="w-4 h-4 text-red-400" />}
                        <span className="text-white text-sm font-medium">{item.type === 'image' ? 'Image' : 'Video'}</span>
                      </div>
                      
                      {editingIndex === index ? (
                        <div className="flex items-center gap-2 mt-2">
                          <Input type="text" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} placeholder="Enter description..." className="bg-slate-900 border-slate-700 text-white text-sm h-8 flex-1" autoFocus onKeyDown={(e) => { if (e.key === 'Enter') saveDescriptionEdit(index); if (e.key === 'Escape') cancelEditDescription(); }} />
                          <Button size="sm" onClick={() => saveDescriptionEdit(index)} className="h-8 w-8 p-0 bg-green-500 hover:bg-green-600"><Check className="w-4 h-4" /></Button>
                          <Button size="sm" variant="outline" onClick={cancelEditDescription} className="h-8 w-8 p-0 border-slate-700"><X className="w-4 h-4" /></Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mt-1 cursor-pointer group" onClick={() => startEditDescription(index)}>
                          {item.description ? <p className="text-slate-300 text-sm">{item.description}</p> : <p className="text-slate-500 text-sm italic">No description</p>}
                          <Edit2 className="w-3 h-3 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      )}
                      
                      <p className="text-slate-500 text-xs truncate mt-1">{item.url}</p>
                      {/* Per-slide duration */}
                      <div className="flex items-center gap-2 mt-2">
                        <Clock className="w-3 h-3 text-slate-500" />
                        <select
                          value={item.duration || ''}
                          onChange={(e) => {
                            const updated = [...content];
                            updated[index] = { ...updated[index], duration: e.target.value ? parseInt(e.target.value) : undefined };
                            saveContent(updated);
                          }}
                          className="bg-slate-900 border border-slate-700 text-white rounded text-xs px-2 py-1"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`slide-duration-${index}`}
                        >
                          <option value="">Default</option>
                          <option value="3">3s</option>
                          <option value="5">5s</option>
                          <option value="10">10s</option>
                          <option value="15">15s</option>
                          <option value="20">20s</option>
                          <option value="30">30s</option>
                          <option value="45">45s</option>
                          <option value="60">60s</option>
                          <option value="90">90s</option>
                          <option value="120">2m</option>
                        </select>
                        {item.duration && <span className="text-cyan-400 text-xs">{item.duration}s</span>}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <Button onClick={() => handleMoveUp(index)} disabled={index === 0} size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-30 h-8 w-8 p-0"><ChevronUp className="w-4 h-4" /></Button>
                      <Button onClick={() => handleMoveDown(index)} disabled={index === content.length - 1} size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-30 h-8 w-8 p-0"><ChevronDown className="w-4 h-4" /></Button>
                      <Button onClick={() => handleRemoveItem(index)} size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Display Orientation */}
        <Card className="bg-slate-900 border-slate-800 mt-8">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Monitor className="w-5 h-5 text-cyan-400" /> Display Orientation
            </h3>
            <div className="flex gap-3">
              <Button
                variant={orientation === 'landscape' ? 'default' : 'outline'}
                className={orientation === 'landscape' ? 'bg-cyan-500 hover:bg-cyan-600' : 'border-slate-600 text-slate-300 hover:bg-slate-700'}
                onClick={() => toggleOrientation('landscape')}
                data-testid="orientation-landscape"
              >
                <Monitor className="w-4 h-4 mr-2" /> Landscape
              </Button>
              <Button
                variant={orientation === 'portrait' ? 'default' : 'outline'}
                className={orientation === 'portrait' ? 'bg-cyan-500 hover:bg-cyan-600' : 'border-slate-600 text-slate-300 hover:bg-slate-700'}
                onClick={() => toggleOrientation('portrait')}
                data-testid="orientation-portrait"
              >
                <Smartphone className="w-4 h-4 mr-2" /> Portrait
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Time-Based Playlists */}
        <Card className="bg-slate-900 border-slate-800 mt-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <Clock className="w-5 h-5 text-cyan-400" /> Time-Based Playlists
                </h3>
                <p className="text-slate-400 text-sm mt-1">Schedule different content for different times of day</p>
              </div>
              <Switch
                checked={usePlaylists}
                onCheckedChange={toggleUsePlaylists}
                data-testid="playlist-toggle"
              />
            </div>
            {usePlaylists && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-300 text-sm">Configured Playlists:</span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-cyan-500 text-cyan-400"
                    onClick={() => {
                      setEditingPlaylist({ items: [], isNew: true });
                      setPlaylistName('');
                      setPlaylistStartHour(0);
                      setPlaylistEndHour(24);
                    }}
                    data-testid="add-playlist-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Playlist
                  </Button>
                </div>
                {playlists.length === 0 ? (
                  <p className="text-slate-500 text-sm">No playlists configured. Add one to get started.</p>
                ) : (
                  <div className="space-y-2">
                    {playlists.map((playlist, index) => (
                      <div key={index} className="flex items-center justify-between bg-slate-950 p-3 rounded-lg">
                        <div>
                          <span className="text-white font-medium">{playlist.name}</span>
                          <span className="text-slate-400 text-sm ml-2">
                            {playlist.start_hour}:00 - {playlist.end_hour}:00 UTC
                          </span>
                          <span className="text-slate-500 text-sm ml-2">({playlist.items?.length || 0} items)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="ghost" className="text-cyan-400" onClick={() => startEditPlaylist(playlist, index)}>Edit</Button>
                          <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deletePlaylist(index)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {editingPlaylist && (
                  <div className="mt-4 p-4 bg-slate-950 rounded-lg border border-slate-700">
                    <h5 className="text-white font-medium mb-3">{editingPlaylist.isNew ? 'New Playlist' : 'Edit Playlist'}</h5>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <Input
                        placeholder="Playlist name"
                        value={playlistName}
                        onChange={(e) => setPlaylistName(e.target.value)}
                        className="bg-slate-900 border-slate-700 text-white"
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 text-sm">From:</span>
                        <select value={playlistStartHour} onChange={(e) => setPlaylistStartHour(e.target.value)} className="bg-slate-900 border border-slate-700 text-white rounded-md px-2 py-1 flex-1">
                          {[...Array(24)].map((_, i) => (<option key={i} value={i}>{i}:00</option>))}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 text-sm">To:</span>
                        <select value={playlistEndHour} onChange={(e) => setPlaylistEndHour(e.target.value)} className="bg-slate-900 border border-slate-700 text-white rounded-md px-2 py-1 flex-1">
                          {[...Array(25)].map((_, i) => (<option key={i} value={i}>{i}:00</option>))}
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" className="text-slate-400" onClick={() => setEditingPlaylist(null)}>Cancel</Button>
                      <Button className="bg-cyan-500 hover:bg-cyan-600" onClick={savePlaylist} disabled={!playlistName.trim()}>Save Playlist</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Page Password */}
        <Card className="bg-slate-900 border-slate-800 mt-8">
          <CardContent className="p-6">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Lock className="w-5 h-5 text-cyan-400" /> Page Password
            </h3>
            <div className="flex gap-2">
              <Input
                type={showPasswordField ? 'text' : 'password'}
                value={pagePassword}
                onChange={(e) => setPagePassword(e.target.value)}
                placeholder="Enter new password"
                className="bg-slate-950 border-slate-700 text-white"
                data-testid="page-password-input"
              />
              <Button
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={() => setShowPasswordField(!showPasswordField)}
              >
                {showPasswordField ? 'Hide' : 'Show'}
              </Button>
              <Button
                onClick={updatePassword}
                className="bg-cyan-500 hover:bg-cyan-600"
                data-testid="save-password-btn"
              >
                Save
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* FrostByte Ad Toggle */}
        <div className="mt-8">
          <DefaultAdToggle pageId={pageId} />
        </div>

        {/* Weather Zip Code Config */}
        <div className="mt-8">
          <WeatherZipConfig pageId={pageId} />
        </div>

        {/* Slide Timing */}
        <div className="mt-8">
          <SlideTiming pageId={pageId} />
        </div>

        {/* Connected Displays */}
        <div className="mt-8">
          <ConnectedDisplays pageId={pageId} />
        </div>

        {/* Fully Kiosk Remote Control */}
        <div className="mt-8">
          <FullyKioskControl pageId={pageId} />
        </div>

        {/* Live Stream */}
        <div className="mt-8">
          <LiveStreamConfig pageId={pageId} />
        </div>

        {/* Announcement Bar */}
        <div className="mt-8">
          <AlertConfig pageId={pageId} />
        </div>

        {/* Multi-Zone Layout */}
        <div className="mt-8">
          <ZoneConfig pageId={pageId} />
        </div>

        {/* Live Preview */}
        <div className="mt-8">
          <LivePreview content={content} slideDuration={pageConfig?.slide_duration || 5} />
        </div>

        {/* Quick Links */}
        <div className="flex gap-4 justify-center mt-8">
          <a
            href={`/page/${pageId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyan-400 hover:text-cyan-300 text-sm"
          >
            View {pageConfig.name} Page →
          </a>
          <a href="/" className="text-cyan-400 hover:text-cyan-300 text-sm">
            Back to Home →
          </a>
          <a href="/master-admin" className="text-cyan-400 hover:text-cyan-300 text-sm">
            Master Admin →
          </a>
        </div>
      </div>
    </div>
  );
};

export default DynamicAdmin;
